import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'basic-components';
import { CommonEnums } from 'dashboard-services/api/common';
import { DataStorageEnums } from 'dashboard-services/api/dataStorage';

import { settingsSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { settingsSeriesQueries } from 'queries/series';
import { FormulaUtils } from 'utils';

import { ColumnsOrder, DateFormat, DateOrder, OutputType, SetAsDefaultSettings, TimeAndDate, TimeZone } from 'primary/TsSettings';

import { When } from 'react-if';

import 'primary/TsSettings/ExpandedSettings/Components.scss';
import './Settings.scss';

const Settings = () => {
  const dispatch = useDispatch(),
        [keyQuery, setKeyQuery] = useState(),
        source = useSelector(state => state.globalState.localSource),
        { 
          formatType,
          csvHeaders,
          timeZone,
          range,
          dateFormat,
          order = CommonEnums.SORT_ORDER.DESCENDING,
          shouldTranspose = "false",
          sortKeys = "false", 
          sortKeysBy,
          sortKeysOrder = CommonEnums.SORT_ORDER.ASCENDING,
          formulaExpressions = {}
        } = useSelector(state => state.seriesState.settingsState),
        selectedSymbols = useSelector(state => state.seriesState.creatorState.selectedSymbols),
        onSaveDefaultSettings = useCallback(() => dispatch(settingsSeriesActions.saveDefaultSettings()), [dispatch]),
        onClearDefaultSettings = useCallback(() => dispatch(settingsSeriesActions.clearDefaultSettings()), [dispatch]),
        onChange = useCallback((val, { name }) => dispatch(settingsSeriesActions.onChange(val, name)), [dispatch]),
        targetSource = useMemo(() => {
          if (selectedSymbols?.[0]?.source) {
            return selectedSymbols[0].source
          } else {
            return source
          }
        }, [selectedSymbols, source]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        { isFetching: isFetchingKeys, isPending: isPendingKeys, data: keys } = settingsSeriesQueries.useKeysForGroups({ 
          input: keyQuery,
          selectedSymbols,
          getApiConfig: getApiConfigLocal,
          getMetadataFilter: settingsSeriesActions.getMetadataFilter,
          getSymbolFilter: settingsSeriesActions.getSymbolFilter,
          getSelect: settingsSeriesActions.getSelect,
          provider: targetSource
        })

  return (
    <div className="ng-office-app__authed__content__basket__body__settings">
      <SetAsDefaultSettings onSave={onSaveDefaultSettings}/>
      <OutputType
          csvHeaders={csvHeaders}
          csvHeadersOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.csvHeaders)}
          formatType={formatType}
          formatTypeOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.formatType)}
          isPendingKeys={isPendingKeys || isFetchingKeys}
          keys={keys}
          onChange={onChange}
          setKeyQuery={setKeyQuery}
          shouldTranspose={shouldTranspose}
          shouldTransposeOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.shouldTranspose)}
          withOutputType
          withTranspose
      />
      <TimeAndDate>
        <TimeZone
            onChange={onChange}
            timeZone={timeZone}
            timeZoneOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.timeZone)}
        />
        <DateFormat
            dateFormat={dateFormat}
            dateFormatOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.dateFormat)}
            onChange={onChange}
        />
        <DateOrder
            onChange={onChange}
            order={order}
            orderOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.order)}
            range={range}
        />
      </TimeAndDate>
      <When condition={formatType === DataStorageEnums.DATA_FORMAT.CSV}>
        {() => (
          <ColumnsOrder
              isPendingKeys={isPendingKeys || isFetchingKeys}
              keys={keys}
              onChange={onChange}
              sortKeys={sortKeys}
              sortKeysBy={sortKeysBy}
              sortKeysByOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.sortKeysBy)}
              sortKeysOrder={sortKeysOrder}
              sortKeysOrderOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.sortKeysOrder)}
              sortKeysOverridden={FormulaUtils.isRawFormulaOverridden(formulaExpressions.sortKeys)}
          />
        )}
      </When>
      <div className="ng-office-app__authed__content__basket__body__settings__reset">
        <Button
            onClick={onClearDefaultSettings}
            size="small"
            variant="text"
        >
          Reset default settings
        </Button>
      </div>
    </div>
  )
}

export default Settings;
